import { useMutation, useSuspenseQuery } from "@tanstack/react-query"
import {
  ClientOnly,
  createFileRoute,
  useStableCallback,
} from "@tanstack/react-router"
import { useServerFn } from "@tanstack/react-start"
import type { ColumnDef, VisibilityState } from "@tanstack/react-table"
import { createColumnHelper } from "@tanstack/react-table"
import { TrashIcon } from "lucide-react"
import { useId, useMemo, useState } from "react"

import {
  ActionsDropdownMenu,
  type ActionsDropdownMenuAction,
} from "@repo/ui/components/ui/actions-dropdown-menu"
import { Badge } from "@repo/ui/components/ui/badge"
import { ConfirmationDialog } from "@repo/ui/components/ui/confirmation-dialog"
import { DataTable } from "@repo/ui/components/ui/data-table"
import { If } from "@repo/ui/components/ui/if"
import { toast } from "@repo/ui/components/ui/sonner"
import { useLocalStorage } from "@repo/ui/hooks/use-local-storage"
import { pick } from "@repo/utils/collections"
import { logger } from "@repo/utils/logger"
import { dataTableBaseSearchSchema } from "~/lib/schemas/common"
import { deleteProjectResourceFn } from "~/modules/projects/projects.functions"
import { getExtendedProjectsQueryOptions } from "~/modules/projects/projects.queries"
import {
  getExtendedResourceBySlugQueryOptions,
  getExtendedResourcesQueryOptions,
} from "~/modules/resources/resources.queries"

const log = logger.child({
  module: "resources-resource-projects-route",
})

const searchSchema = dataTableBaseSearchSchema

export const Route = createFileRoute(
  "/_protected-layout/_os-layout/resources/$resourceSlug/projects",
)({
  component: RouteComponent,
  validateSearch: searchSchema,
  loaderDeps: ({ search }) =>
    pick(search, ["q", "page", "page_size", "sorting", "columnFilters"]),
  loader: async ({ context, params, deps }) => {
    context.queryClient
      .ensureQueryData(
        getExtendedResourceBySlugQueryOptions({ slug: params.resourceSlug }),
      )
      .catch((error) => {
        log.error("Error getting extended resource by slug", { error })
        return null
      })

    return {
      filters: deps,
    }
  },
})

type ProjectData = {
  readonly id: string
  readonly project_resource_id: string
  readonly project_name: string
  readonly client_id: string
  readonly status: string
  readonly project_start_date: string
  readonly project_end_date: string | null
}

const TABLE_VISIBLE_COLUMNS_KEY = "resource-projects-visible-columns"

const columnHelper = createColumnHelper<ProjectData>()

function RouteComponent() {
  const params = Route.useParams()
  const { filters } = Route.useLoaderData()
  const { data: resource } = useSuspenseQuery(
    getExtendedResourceBySlugQueryOptions({ slug: params.resourceSlug }),
  )

  const [visibleColumns, setVisibleColumns] = useLocalStorage<VisibilityState>(
    TABLE_VISIBLE_COLUMNS_KEY,
    {},
  )

  const toastId = useId()
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false)
  const [projectToRemove, setProjectToRemove] = useState<ProjectData | null>(
    null,
  )

  const deleteProjectResource = useServerFn(deleteProjectResourceFn)

  const deleteProjectResourceMutation = useMutation({
    mutationFn: deleteProjectResource,
    meta: {
      invalidates: [
        getExtendedResourcesQueryOptions().queryKey,
        getExtendedProjectsQueryOptions().queryKey,
      ],
    },
    onMutate: () => {
      toast.loading("Removing from project...", { id: toastId })
    },
    onSuccess: (result) => {
      if (!result.success) {
        toast.error(result.message, { id: toastId })
        return
      }

      toast.success("Removed from project", { id: toastId })
      setRemoveDialogOpen(false)
      setProjectToRemove(null)
    },
    onError: (error) => {
      toast.error("Error removing from project", {
        id: toastId,
        description: error.message,
      })
    },
  })

  const handleRemoveClick = useStableCallback((project: ProjectData) => {
    setProjectToRemove(project)
    setRemoveDialogOpen(true)
  })

  const handleConfirmRemove = () => {
    if (projectToRemove) {
      deleteProjectResourceMutation.mutate({
        data: { projectResourceId: projectToRemove.project_resource_id },
      })
    }
  }

  // Transform project_resources data for the table
  const projectsData: ProjectData[] = useMemo(
    () =>
      resource?.project_resources?.map((pr) => ({
        id: pr.project_id,
        project_resource_id: pr.id,
        project_name: pr.project_name,
        client_id: pr.client_id,
        status: pr.status,
        project_start_date: pr.project_start_date,
        project_end_date: pr.project_end_date,
      })) || [],
    [resource?.project_resources],
  )

  // biome-ignore lint/suspicious/noExplicitAny: please
  const columns: ColumnDef<ProjectData, any>[] = useMemo(
    () => [
      columnHelper.accessor("project_name", {
        id: "project_name",
        header: () => <div className="min-w-[200px]">Project</div>,
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("project_start_date", {
        id: "project_start_date",
        header: () => <div className="min-w-[120px]">Start Date</div>,
        cell: ({ row }) => {
          return (
            <ClientOnly>
              <span>
                {new Date(
                  row.getValue("project_start_date"),
                ).toLocaleDateString()}
              </span>
            </ClientOnly>
          )
        },
      }),
      columnHelper.accessor("project_end_date", {
        id: "project_end_date",
        header: () => <div className="min-w-[120px]">End Date</div>,
        cell: ({ row }) => {
          const endDate = row.getValue("project_end_date") as string | null

          return (
            <ClientOnly>
              <span>
                {!endDate ? "Ongoing" : new Date(endDate).toLocaleDateString()}
              </span>
            </ClientOnly>
          )
        },
      }),
      columnHelper.accessor("status", {
        id: "status",
        header: () => <div className="w-[100px]">Status</div>,
        cell: ({ row }) => {
          const status = row.getValue("status") as string
          return (
            <Badge variant={status === "active" ? "default" : "secondary"}>
              {status}
            </Badge>
          )
        },
        meta: {
          filterVariant: "select",
          filterSelectOptions: [
            { label: "Active", value: "active" },
            { label: "Inactive", value: "inactive" },
            { label: "Completed", value: "completed" },
          ],
        },
      }),
      columnHelper.display({
        id: "actions",
        header: () => <div className="w-[80px] text-right">Actions</div>,
        enableHiding: false,
        cell: ({ row }) => {
          const project = row.original

          const actions: ActionsDropdownMenuAction[] = [
            {
              label: "Remove from Project",
              icon: TrashIcon,
              onClick: () => handleRemoveClick(project),
              variant: "destructive",
            },
          ]

          return (
            <div className="flex justify-end">
              <ActionsDropdownMenu groups={[{ actions, separator: false }]} />
            </div>
          )
        },
      }),
    ],
    [handleRemoveClick],
  )

  if (!resource) {
    return <div>Resource not found</div>
  }

  return (
    <>
      <If
        condition={projectsData}
        fallback={
          <div className="h-full flex-1 overflow-auto">
            <div className="flex h-64 items-center justify-center">
              <div className="text-white/70">Loading projects...</div>
            </div>
          </div>
        }
      >
        {(data) => (
          <DataTable
            data={data}
            columns={columns}
            searchPlaceholder="Search projects..."
            noResultsMessage="No projects found"
            manualPagination={false}
            manualSorting={false}
            manualFiltering={false}
            getInitialColumnVisibility={() => visibleColumns}
            onColumnVisibilityChange={(visibility) =>
              setVisibleColumns(visibility)
            }
            getInitialColumnFilters={() => filters.columnFilters ?? []}
            getInitialSorting={() => filters.sorting ?? []}
          />
        )}
      </If>

      {/* Remove Confirmation Dialog */}
      <ConfirmationDialog
        open={removeDialogOpen}
        onOpenChange={setRemoveDialogOpen}
        title="Remove from Project"
        description="Are you sure you want to remove this resource from the project? This action cannot be undone."
        onConfirm={handleConfirmRemove}
        isLoading={deleteProjectResourceMutation.isPending}
        loadingText="Removing..."
        confirmText="Remove"
        buttonVariant="destructive"
      >
        {projectToRemove && (
          <div className="flex flex-col gap-2 rounded-lg border border-white/10 bg-white/5 p-4">
            <div className="text-sm text-white/70">Project</div>
            <div className="font-semibold text-white">
              {projectToRemove.project_name}
            </div>
          </div>
        )}
      </ConfirmationDialog>
    </>
  )
}
